/**
 * Umlage der Betriebskosten auf Belegungsabschnitte.
 *
 * Kernbegriff: BELEGUNGSABSCHNITT. Der Abrechnungszeitraum wird je Einheit lückenlos in
 * Abschnitte zerlegt — jeder Abschnitt gehört entweder zu einem Mietverhältnis oder ist
 * Leerstand bzw. Eigennutzung. Der Vermieter trägt Leerstands- und Eigennutzungsanteile
 * selbst; sie werden ausgewiesen, nicht auf die übrigen Mieter verteilt.
 */

import { Cent, verteileNachGewichten } from './geld';
import { personentage } from './mietverlauf';
import { ISODate, Zeitraum, schnitt, tage } from './zeit';

export type Schluessel = 'wohnflaeche' | 'personen' | 'einheiten' | 'verbrauch' | 'meg';

export interface Einheit {
  id: string;
  bezeichnung: string;
  wohnflaecheQm: number;
  megAnteil?: number;
  eigennutzung?: boolean;
}

export interface Mietverhaeltnis {
  id: string;
  einheitId: string;
  beginn: ISODate;
  /** null = laufend */
  ende: ISODate | null;
  /** Personenzahl ab Mietbeginn */
  personenzahl: number;
  /** Änderungen der Personenzahl ab Stichtag (Einzug/Auszug von Mitbewohnern). */
  staende?: Array<{ ab: ISODate; personenzahl: number }>;
}

export interface Kostenposition {
  kostenartCode: string;
  bezeichnung: string;
  schluessel: Schluessel;
  betragCent: Cent;
  /** Pflicht bei schluessel === 'verbrauch': Verbrauch je Belegungsabschnitt-ID. */
  verbrauchJeAbschnitt?: Record<string, number>;
  erlaeuterung?: string;
}

export interface Belegungsabschnitt {
  /** stabile ID: `${einheitId}:${von}` */
  id: string;
  einheitId: string;
  /** null = Leerstand oder Eigennutzung (Vermieter trägt) */
  mietverhaeltnisId: string | null;
  von: ISODate;
  bis: ISODate;
  tage: number;
  personenzahl: number;
  /**
   * Personenzahl × Tage im Abschnitt, wenn sich die Personenzahl innerhalb des Abschnitts
   * ändert. Fehlt der Wert, gilt `personenzahl` für den ganzen Abschnitt.
   */
  personentage?: number;
  vermieterTraegt: boolean;
  grund?: 'leerstand' | 'eigennutzung';
}

export interface UmlageZeile {
  abschnittId: string;
  einheitId: string;
  mietverhaeltnisId: string | null;
  kostenartCode: string;
  schluessel: Schluessel;
  gesamtkostenCent: Cent;
  gesamtBasis: number;
  anteilBasis: number;
  zeitanteilTage: number;
  zeitraumTage: number;
  anteilCent: Cent;
  vermieterTraegt: boolean;
  /**
   * Nur beim Personenschlüssel, wenn sich im Zeitraum irgendwo die Personenzahl ändert:
   * Personentage dieses Abschnitts und aller Abschnitte (für den Ausweis im PDF).
   */
  personentage?: number;
  gesamtPersonentage?: number;
}

export interface UmlageOptionen {
  /**
   * Personenzahl, die für einen leerstehenden Abschnitt beim Personenschlüssel angesetzt wird.
   * Der Anteil trägt der Vermieter. Default 1.
   */
  leerstandPersonen?: number;
}

/* ------------------------------------------------------------------ Abschnitte */

function addTag(d: ISODate, n: number): ISODate {
  const [y, m, day] = d.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, day + n));
  return t.toISOString().slice(0, 10);
}

/**
 * Zerlegt den Abrechnungszeitraum je Einheit lückenlos in Belegungsabschnitte.
 * Überlappende Mietverhältnisse an derselben Einheit sind ein Datenfehler und werfen.
 */
export function belegungsabschnitte(
  zeitraum: Zeitraum,
  einheiten: Einheit[],
  mietverhaeltnisse: Mietverhaeltnis[],
  optionen: UmlageOptionen = {},
): Belegungsabschnitt[] {
  const leerstandPersonen = optionen.leerstandPersonen ?? 1;
  const out: Belegungsabschnitt[] = [];

  for (const einheit of einheiten) {
    const mvs = mietverhaeltnisse
      .filter((m) => m.einheitId === einheit.id)
      .map((m) => ({ mv: m, s: schnitt({ von: m.beginn, bis: m.ende ?? zeitraum.bis }, zeitraum) }))
      .filter((x): x is { mv: Mietverhaeltnis; s: Zeitraum } => x.s !== null)
      .sort((a, b) => (a.s.von < b.s.von ? -1 : 1));

    for (let i = 1; i < mvs.length; i++) {
      if (mvs[i].s.von <= mvs[i - 1].s.bis) {
        throw new Error(
          `Überlappende Mietverhältnisse an Einheit ${einheit.bezeichnung}: ` +
            `${mvs[i - 1].mv.id} und ${mvs[i].mv.id}`,
        );
      }
    }

    let cursor = zeitraum.von;
    const grund = einheit.eigennutzung ? 'eigennutzung' : 'leerstand';

    for (const { mv, s } of mvs) {
      if (cursor < s.von) {
        out.push(mkLeer(einheit, cursor, addTag(s.von, -1), leerstandPersonen, grund));
      }
      const mitWechsel = (mv.staende ?? []).some((x) => x.ab > s.von && x.ab <= s.bis);
      const verlauf = {
        beginn: mv.beginn,
        ende: mv.ende,
        grundwerte: { kaltmieteCent: 0, vzNebenkostenCent: 0, vzHeizkostenCent: 0, personenzahl: mv.personenzahl },
        staende: (mv.staende ?? []).map((x) => ({
          ab: x.ab,
          kaltmieteCent: 0,
          vzNebenkostenCent: 0,
          vzHeizkostenCent: 0,
          personenzahl: x.personenzahl,
        })),
      };
      out.push({
        id: `${einheit.id}:${s.von}`,
        einheitId: einheit.id,
        mietverhaeltnisId: mv.id,
        von: s.von,
        bis: s.bis,
        tage: tage(s),
        // Personenzahl zu Abschnittsbeginn; bei Wechsel zählen die Personentage.
        personenzahl: personentage(verlauf, { von: s.von, bis: s.von }),
        ...(mitWechsel ? { personentage: personentage(verlauf, s) } : {}),
        vermieterTraegt: false,
      });
      cursor = addTag(s.bis, 1);
    }

    if (cursor <= zeitraum.bis) {
      out.push(mkLeer(einheit, cursor, zeitraum.bis, leerstandPersonen, grund));
    }
  }

  return out;

  function mkLeer(
    e: Einheit,
    von: ISODate,
    bis: ISODate,
    personen: number,
    grund: 'leerstand' | 'eigennutzung',
  ): Belegungsabschnitt {
    return {
      id: `${e.id}:${von}`,
      einheitId: e.id,
      mietverhaeltnisId: null,
      von,
      bis,
      tage: tage({ von, bis }),
      personenzahl: personen,
      vermieterTraegt: true,
      grund,
    };
  }
}

/* ------------------------------------------------------------------ Gewichte */

/**
 * Bemessungsgrundlage eines Abschnitts für einen Umlageschlüssel.
 * Alle flächen-, einheiten-, personen- und MEG-basierten Schlüssel werden tagegenau
 * zeitgewichtet. Verbrauch wird NICHT zeitgewichtet — er kommt aus der Zwischenablesung
 * und enthält die Zeitkomponente bereits.
 */
export function basisFuerAbschnitt(
  abschnitt: Belegungsabschnitt,
  einheit: Einheit,
  pos: Kostenposition,
  zeitraumTage: number,
): number {
  const zeitfaktor = abschnitt.tage / zeitraumTage;

  switch (pos.schluessel) {
    case 'wohnflaeche':
      return einheit.wohnflaecheQm * zeitfaktor;
    case 'einheiten':
      return 1 * zeitfaktor;
    case 'personen':
      return abschnitt.personentage !== undefined
        ? abschnitt.personentage / zeitraumTage
        : abschnitt.personenzahl * zeitfaktor;
    case 'meg': {
      if (einheit.megAnteil === undefined) {
        throw new Error(`Einheit ${einheit.bezeichnung} hat keinen Miteigentumsanteil`);
      }
      return einheit.megAnteil * zeitfaktor;
    }
    case 'verbrauch': {
      const v = pos.verbrauchJeAbschnitt?.[abschnitt.id];
      if (v === undefined) {
        throw new Error(
          `Kostenart "${pos.bezeichnung}" wird nach Verbrauch umgelegt, ` +
            `für Abschnitt ${abschnitt.id} fehlt jedoch der Verbrauchswert.`,
        );
      }
      return v;
    }
  }
}

/* ------------------------------------------------------------------ Umlage */

/** Verteilt eine einzelne Kostenposition verlustfrei auf die Belegungsabschnitte. */
export function verteileKostenposition(
  pos: Kostenposition,
  abschnitte: Belegungsabschnitt[],
  einheiten: Einheit[],
  zeitraum: Zeitraum,
): UmlageZeile[] {
  const zeitraumTage = tage(zeitraum);
  const byId = new Map(einheiten.map((e) => [e.id, e]));

  const basen = abschnitte.map((a) => {
    const e = byId.get(a.einheitId);
    if (!e) throw new Error(`Unbekannte Einheit ${a.einheitId}`);
    return basisFuerAbschnitt(a, e, pos, zeitraumTage);
  });

  const gesamtBasis = basen.reduce((x, y) => x + y, 0);
  if (gesamtBasis === 0) {
    throw new Error(
      `Kostenart "${pos.bezeichnung}": Bemessungsgrundlage ist 0. ` +
        'Die Kosten lassen sich mit diesem Umlageschlüssel nicht verteilen.',
    );
  }

  const anteile = verteileNachGewichten(pos.betragCent, basen);
  const mitPersonentagen =
    pos.schluessel === 'personen' && abschnitte.some((a) => a.personentage !== undefined);

  return abschnitte.map((a, i) => ({
    abschnittId: a.id,
    einheitId: a.einheitId,
    mietverhaeltnisId: a.mietverhaeltnisId,
    kostenartCode: pos.kostenartCode,
    schluessel: pos.schluessel,
    gesamtkostenCent: pos.betragCent,
    gesamtBasis: round3(gesamtBasis),
    anteilBasis: round3(basen[i]),
    zeitanteilTage: a.tage,
    zeitraumTage,
    anteilCent: anteile[i],
    vermieterTraegt: a.vermieterTraegt,
    ...(mitPersonentagen
      ? {
          personentage: Math.round(basen[i] * zeitraumTage),
          gesamtPersonentage: Math.round(gesamtBasis * zeitraumTage),
        }
      : {}),
  }));
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
